<script lang="ts">
  import { profileActions, profileStore } from '@/stores/game/profile';
  import { itemsDB } from '@/game/database';
  import { animations } from '@/utils/animations';
  import { gameEventBus } from '@/game/events';
  import { audioManager } from '@/engine/audio';
  import { getItemIconUrl } from '@/assets/icons';
  import Modal from '../base/Modal.svelte';
  import Button from '../base/Button.svelte';
  import CurrencyPanel from '../base/CurrencyPanel.svelte';
  import { onMount, onDestroy } from 'svelte';

  let { isOpen = $bindable(false) } = $props();

  // Reactive statement to get current inventory from store
  const inventoryItems = $derived.by(() => {
    return Object.values($profileStore.inventory.items);
  });

  const sellableItems = $derived.by(() => {
    return inventoryItems
      .filter(item => item.type === 'ore' && item.quantity > 0)
      .sort((a, b) => getItemTier(a.itemId) - getItemTier(b.itemId));
  });

  let unsubscribeEvents: (() => void)[] = [];

  onMount(() => {
    // Подписываемся на события открытия/закрытия модала
    unsubscribeEvents.push(
      gameEventBus.on('warehouse-open', () => {
        isOpen = true;
        // Звуковой эффект открытия
        audioManager.playSFX('ui_click', { volume: 0.5 });
      }),
      gameEventBus.on('warehouse-close', () => {
        isOpen = false;
        // Звуковой эффект закрытия
        audioManager.playSFX('ui_click', { volume: 0.3 });
      })
    );
  });

  onDestroy(() => {
    // Отписываемся от всех событий
    unsubscribeEvents.forEach(fn => fn());
  });

  function handleClose() {
    gameEventBus.emit('warehouse-close');
  }

  function getItemName(itemId: string): string {
    return itemsDB.getItemName(itemId);
  }

  function getItemIcon(itemId: string): string {
    const iconPath = itemsDB.getItemIcon(itemId);
    return getItemIconUrl(iconPath);
  }

  function getItemTier(itemId: string): number {
    return itemsDB.getItemTier(itemId);
  }

  function getItemPrice(itemId: string): number {
    return itemsDB.getItemSellPrice(itemId);
  }

  function formatQuantity(quantity: number): string {
    if (quantity >= 1000000) {
      return `${(quantity / 1000000).toFixed(1)}M`;
    } else if (quantity >= 1000) {
      return `${(quantity / 1000).toFixed(1)}K`;
    }
    return quantity.toString();
  }

  function formatPrice(price: number): string {
    if (price >= 1000000) {
      return `$${(price / 1000000).toFixed(1)}M`;
    } else if (price >= 1000) {
      return `$${(price / 1000).toFixed(1)}K`;
    }
    return `$${price}`;
  }

  function handleSellAll(itemId: string, event: CustomEvent) {
    const item = inventoryItems.find(i => i.itemId === itemId);
    if (!item || item.quantity <= 0) return;

    const price = getItemPrice(itemId);
    const totalEarned = price * item.quantity;

    // Звуковой эффект продажи
    audioManager.playSFX('collect_coin', { volume: 0.7 });

    // Продаем все ресурсы
    profileActions.removeInventoryItem(itemId, item.quantity);
    profileActions.addInventoryItem('credits', totalEarned);

    // Анимация продажи
    const element = document.querySelector(`[data-resource="${itemId}"]`);
    if (element) {
      animations.resourceGain(element as HTMLElement);
    }

    // Анимация кнопки
    const button = event.target as HTMLElement;
    if (button) {
      animations.buttonPress(button);
    }

    // Тактильная обратная связь (если доступно)
    if (navigator.vibrate) {
      navigator.vibrate(50);
    }
  }

  function handleSellAllItems(event: CustomEvent) {
    let totalEarned = 0;

    // Звуковой эффект массовой продажи
    audioManager.playSFX('collect_coin', { volume: 0.9 });

    for (const item of sellableItems) {
      const price = getItemPrice(item.itemId);
      const earned = price * item.quantity;
      totalEarned += earned;
      profileActions.removeInventoryItem(item.itemId, item.quantity);
    }

    if (totalEarned > 0) {
      profileActions.addInventoryItem('credits', totalEarned);
    }

    // Анимация кнопки
    const button = event.target as HTMLElement;
    if (button) {
      animations.buttonPress(button);
    }

    // Тактильная обратная связь (если доступно)
    if (navigator.vibrate) {
      navigator.vibrate([50, 100, 50]);
    }
  }
</script>

<Modal {isOpen} title="Warehouse" size="lg" maxHeightVh={85} on:close={handleClose}>
  <div class="space-y-4 w-full h-full flex flex-col overflow-hidden">
    <div class="flex flex-col items-center gap-4 flex-shrink-0">
      <p class="text-gray-300 text-base max-w-[600px] mx-auto text-center">
        Sell your ores for credits to upgrade your mining equipment
      </p>
      <CurrencyPanel size="md" showLabel={true} />
    </div>

    <div
      class="space-y-3 flex-1 overflow-y-auto overflow-x-hidden"
      style="scrollbar-width: thin; scrollbar-color: var(--color-neon-green) transparent;"
    >
      {#each sellableItems as item (item.itemId)}
        <div
          class="bg-gradient-to-r from-gray-800/60 to-gray-800/40 rounded-xl p-4 border border-gray-700/50 hover:border-neon-green/40 hover:shadow-lg hover:shadow-neon-green/10 transition-all duration-300 hover:scale-[1.01] grid grid-cols-[auto_1fr_auto_auto] gap-4 items-center backdrop-blur-sm min-h-[70px] w-full max-w-[700px] mx-auto"
          data-resource={item.itemId}
        >
          <div
            class="flex-shrink-0 w-16 flex items-center justify-center transition-transform duration-300 hover:scale-110"
          >
            <img
              src={getItemIcon(item.itemId)}
              alt={item.itemId}
              class="w-16 h-16 object-contain transition-all duration-300"
              style="filter: drop-shadow(0 0 4px rgba(0, 255, 255, 0.6));"
            />
          </div>

          <div class="min-w-0">
            <div
              class="text-white font-semibold text-base transition-colors duration-300 hover:text-neon-green"
            >
              {getItemName(item.itemId)}
            </div>
            <div class="text-gray-400 text-sm transition-colors duration-300 hover:text-gray-300">
              {formatPrice(getItemPrice(item.itemId))} each
            </div>
          </div>

          <div class="flex-shrink-0 text-right w-20 flex items-center justify-end">
            <span
              class="text-neon-green font-bold text-xl transition-all duration-300 hover:text-neon-yellow"
              style="text-shadow: 0 0 4px var(--color-neon-green);"
              >{formatQuantity(item.quantity)}</span
            >
          </div>

          <div class="flex justify-end">
            <Button
              variant="success"
              size="sm"
              disabled={item.quantity <= 0}
              on:click={e => handleSellAll(item.itemId, e)}
            >
              Sell All
            </Button>
          </div>
        </div>
      {/each}
    </div>

    {#if sellableItems.length > 0}
      <div class="flex justify-center flex-shrink-0 mb-1 px-4">
        <Button variant="danger" size="md" on:click={handleSellAllItems}>Sell Everything</Button>
      </div>
    {/if}

    {#if sellableItems.length === 0}
      <div class="text-center flex-1 flex flex-col items-center justify-center text-gray-400">
        <div class="text-5xl mb-4 animate-bounce">⛏️</div>
        <p class="text-xl font-semibold">No ores available for sale</p>
        <p class="text-base mt-4 text-gray-500">Mine some asteroids to get ores!</p>
        <div class="mt-6">
          <Button variant="primary" size="md" on:click={handleClose}>Start Mining</Button>
        </div>
      </div>
    {/if}
  </div>
</Modal>

<style lang="postcss">
  @reference "@/styles/theme.css";

  /* Hover effects for resource items */
  .grid:hover img {
    filter: drop-shadow(0 0 8px rgba(0, 255, 255, 0.8)) !important;
  }

  /* Enhanced button styles */
  :global(.btn) {
    @apply transition-all duration-300;
    @apply hover:shadow-lg;
    @apply active:scale-95;
  }
</style>
